import { message } from 'antd'
import { useEffect } from 'react'
import { MyForm } from '~/components/ui/MyForm'
import { GET_AI_MEMBER_LIST, GET_AI_TOTAL_INFO } from '~/graphqls/gqls'
import {
  AiArtworkModelFusionInput,
  UserTrainedModelInput,
  AiArtworkOptionInput,
  AiCreateArtworkModel,
  AiCreateArtworkPattern,
  MJQuality,
  MjParamInput,
  PretreatmentMethod,
  QueueType,
  SuperType,
  getAIMemberList,
  getAIMemberListVariables,
  getAITotalInfo,
  getAITotalInfo_pictureGraph_aiArtworkTotalInfo,
  LayerDiffusionInput,
  ControlShowType,
  InstantStyle,
  SpellAnalysisImgType,
  ControlNetPulidMode,
  CutDiffusionInput,
  AiItemPostTargetEnum,
  AiTypeEnum,
  FluxInput,
} from '~/graphqls/types'

import { useAtom } from 'jotai'
import { observer } from 'mobx-react'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useStores } from '~/hooks/useStores'
import { clearStorage, getStorage, isNullOrUndefined } from '~/utils'
import styles from './AiForm.module.css'
import { AgreementComp } from './ai-form/AgreementComp'
import { CreateComp } from './ai-form/CreateComp'
import { GenerationComp } from './ai-form/GenerationComp'
import { PaintTimesComp } from './ai-form/PaintTimesComp'
import {
  DEFAULT_OPTIONS,
  cfgAtom,
  characterAtom,
  clipSkipAtom,
  controlPrefineAtom,
  creativityDegreeAtom,
  doRepairTheHandAtom,
  endingStepAtom,
  ensdAtom,
  genTidalAtom,
  hasAdvanceAtom,
  initImageUrlAtom,
  maskImageUrlAtom,
  memberListAtom,
  mjQualityAtom,
  mjTileAtom,
  mjUpbetaAtom,
  modelCodeAtom,
  modelFusionAtom,
  optionsAtom,
  paintRefAtom,
  paintTimesAtom,
  patternAtom,
  pretreatmentMethodAtom,
  promptAtom,
  resolutionKeyAtom,
  seedAtom,
  selectModelAtom,
  selectResolutionAtom,
  selectSamplerAtom,
  selectSizeAtom,
  styleArtisticAtom,
  styleDecorationAtom,
  styleDiffAtom,
  styleModelAtom,
  totalInfoAtom,
  trackEventLabelAtom,
  transparentWeightAtom,
  ucPromptAtom,
  referToStyleAtom,
  referToRoleAtom,
  referToStructureAtom,
  spellAnalysisImgTypeAtom,
  cfgScale1Atom,
  cfgScale2Atom,
  cutDiffProportionAtom,
  initImageSourcesAtom,
  styleAtom,
  choasAtom,
} from './ai-form/atom'
import { useForm } from './ai-form/useForm'
import useOptions from './ai-form/useOptions'
import { ControlnetFormContent } from './ai-homepage/ControlnetFormContent'
import { MaskFormContent } from './ai-homepage/MaskFormContent'
import { NormalFormContent } from './ai-homepage/NormalFormContent'
import { useType } from './useType'
import { TransparentFormContent } from './ai-homepage/TransparentFormContent'

export interface FormParamsProps {
  prompt: string
  size: string
  width: number
  length: number
  model?: AiCreateArtworkModel
  styleModel?: string | null
  initImageUrl?: string
  creativityDegree?: number
  num?: number
  maskImageUrl?: string
  maskZoneImageUrl?: string
  pattern: AiCreateArtworkPattern
  modelCode: number
  ucPrompt?: string
  doRepairTheHand?: boolean
  styleDecorationOptions?: AiArtworkOptionInput[]
  character?: string[]
  modelFusion?: AiArtworkModelFusionInput[]
  userTrainedModel?: UserTrainedModelInput[]
  initWidth?: number
  initHeight?: number
  superType?: SuperType | null
  cfg?: number | null
  seed?: string | null
  samplerIndex?: number | null
  steps?: number | null
  /** 线稿上色模式特有参数：预处理方式 */
  pretreatmentMethod?: PretreatmentMethod
  /** 百度统计事件 */
  trackEventLabel?: string | null
  isGPT?: boolean
  ensd?: number
  clipSkip?: number
  queueType?: QueueType
  mjParam?: MjParamInput
  /** 透明图参数 */
  layerDiffusion?: LayerDiffusionInput
  /** 各种参考 */
  instantStyle?: InstantStyle[]
  spellAnalysisImgType?: SpellAnalysisImgType
  cutdiffusion?: CutDiffusionInput
  flux?: FluxInput
}

export const AiForm = observer(() => {
  const { store } = useStores()
  const [options] = useAtom(optionsAtom)
  const [, setTotalInfo] = useAtom(totalInfoAtom)
  const [, setMemberList] = useAtom(memberListAtom)
  const [selectModel, setSelectModel] = useAtom(selectModelAtom)
  const [modelCode, setModelCode] = useAtom(modelCodeAtom)
  const [pattern] = useAtom(patternAtom)
  /** 创意度 */
  const [creativityDegree, setCreativityDegree] = useAtom(creativityDegreeAtom)
  /** 融合模型 */
  const [modelFusion, setModelFusion] = useAtom(modelFusionAtom)
  const [, setResolutionKey] = useAtom(resolutionKeyAtom)
  /** 画面大小 */
  const [selectSize, setSelectSize] = useAtom(selectSizeAtom)
  /** 画面尺寸 */
  const [selectResolution, setSelectResolution] = useAtom(selectResolutionAtom)
  /** 标签选择 */
  const [styleDecoration, setStyleDecoration] = useAtom(styleDecorationAtom)
  /** 高级设置 */
  const [hasAdvance, setHasAdvance] = useAtom(hasAdvanceAtom)
  /** 作画数量 */
  const [paintTimes] = useAtom(paintTimesAtom)
  /** 涂抹图片URL */
  const [, setMaskImageUrl] = useAtom(maskImageUrlAtom)
  /** 涂抹底图来源 */
  const [initImageSources, setInitImageSources] = useAtom(initImageSourcesAtom)
  /** 修手 */
  const [doRepairTheHand, setDoRepairTheHand] = useAtom(doRepairTheHandAtom)
  /** 随机种子 */
  const [seed] = useAtom(seedAtom)
  /** 采样模式 */
  const [selectSampler, setSelectSampler] = useAtom(selectSamplerAtom)
  /** 底图 */
  const [initImageUrl, setInitImageUrl] = useAtom(initImageUrlAtom)
  /** 角色同人 */
  const [character, setCharacter] = useAtom(characterAtom)
  const [ucPrompt, setUcPrompt] = useAtom(ucPromptAtom)
  const [prompt, setPrompt] = useAtom(promptAtom)
  const [trackEventLabel, setTrackEventLabel] = useAtom(trackEventLabelAtom)
  const [cfg, setCfg] = useAtom(cfgAtom)
  const [styleModel, setStyleModel] = useAtom(styleModelAtom)
  const [ensd, setEnsd] = useAtom(ensdAtom)
  const [clipSkip, setClipSkip] = useAtom(clipSkipAtom)
  /** ifg */
  const [style, setStyle] = useAtom(styleAtom)
  const [chaos, setChaos] = useAtom(choasAtom)

  /** 透明图：权重 */
  const [transparentWeight, setTransparentWeight] = useAtom(
    transparentWeightAtom
  )
  /** 透明图：Stop At */
  const [endingStep, setEndingStep] = useAtom(endingStepAtom)
  /** 预处理 */
  const [pretreatmentMethod, setPretreatmentMethod] = useAtom(
    pretreatmentMethodAtom
  )
  /** xx参考 */
  const [referToRole, setReferToRole] = useAtom(referToRoleAtom)
  const [referToStyle, setReferToStyle] = useAtom(referToStyleAtom)
  const [referToStructure, setReferToStructure] = useAtom(referToStructureAtom)
  const [spellAnalysisImgType] = useAtom(spellAnalysisImgTypeAtom)

  /** MJ高级 */
  const [styleDiff, setStyleDiff] = useAtom(styleDiffAtom)
  const [styleArtistic, setStyleArtistic] = useAtom(styleArtisticAtom)
  const [mjQuality, setMjQuality] = useAtom(mjQualityAtom)
  const [mjUpbeta, setMjUpbeta] = useAtom(mjUpbetaAtom)
  const [mjTile, setMjTile] = useAtom(mjTileAtom)
  const [selectedControlPrefine, setSelectedControlPrefine] =
    useAtom(controlPrefineAtom)
  // 潮汐模式生成
  const [genTidal, setGenTidal] = useAtom(genTidalAtom)

  /** 4k */
  const [cutDiffProportion, setCutDiffProportion] = useAtom(
    cutDiffProportionAtom
  )
  const [cfgScale1, setCfgScale1] = useAtom(cfgScale1Atom)
  const [cfgScale2, setCfgScale2] = useAtom(cfgScale2Atom)

  const [form] = MyForm.useForm()

  /** 涂抹模式专用 */
  const [paintRef] = useAtom(paintRefAtom)

  const {
    isST,
    isMJ,
    isMask,
    isControlnet,
    isGeneral,
    isTransparent,
    isMJ6,
    isMJ7,
    is4k,
  } = useType()

  const { getBestSizeForImage, setPrefineOptions } = useForm()
  const { isSdxl, isFlux } = useOptions()

  const getTotalInfoAction =
    useImperativeQuery<getAITotalInfo>(GET_AI_TOTAL_INFO)

  const getMemberListAction = useImperativeQuery<
    getAIMemberList,
    getAIMemberListVariables
  >(GET_AI_MEMBER_LIST)

  useEffect(() => {
    /** 作画时间等 */
    if (store.ai?.isRefreshTotalInfo) {
      getTotalInfoAction()
        .then(res => {
          setTotalInfo(
            (res.data.pictureGraph
              ?.aiArtworkTotalInfo as getAITotalInfo_pictureGraph_aiArtworkTotalInfo) ||
              {}
          )
        })
        .catch(err => {
          message.destroy()
          message.error('更新AI作画信息失败，请刷新')
        })
        .finally(() => {
          store.ai?.setIsRefreshTotalInfo(false)
        })
    }
  }, [store.ai?.isRefreshTotalInfo])

  useEffect(() => {
    /** 权益卡 */
    if (store.ai?.isRefreshMemberList) {
      getMemberListAction()
        .then(res => {
          setMemberList(res.data.accountModule?.userMemberCardList || [])
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          store.ai?.setIsRefreshMemberList(false)
        })
    }
  }, [store.ai?.isRefreshMemberList])

  /** 再来一张 */
  const again = async () => {
    const aiParams: any = getStorage('aiParam')

    console.log(aiParams, 'aiParams')

    if (!Object.keys(aiParams).length) return

    // 如果在涂抹模式下，将当前图带入待涂抹的图, 同时清空prompt
    if (pattern === AiCreateArtworkPattern.MASK) {
      setPrompt('')
      setMaskImageUrl(aiParams.originUrl)
      setInitImageSources([
        {
          sourceId: aiParams.id,
          applyScene: AiItemPostTargetEnum.INIT_IMAGE,
          sourceAiType: AiTypeEnum.PICTURE,
        },
      ])
    } else {
      setPrompt(aiParams.prompt)
      // 神奇涂抹不需要，因为有lockModelCode
      setModelCode(aiParams.modelCode)
    }

    // 手机照改漫Tab生成的图片没有size，根据底图为其匹配size
    if (!aiParams.size) {
      let size = (await getBestSizeForImage(
        aiParams.initImageUrl ?? aiParams.originUrl
      )) as string

      const curResolution =
        options?.allSizeOptions?.[0]?.sizeSetOptions?.find(
          item => item?.name === size
        )?.resolutionList || []

      setSelectSize(size)
      setSelectResolution(
        `${curResolution?.[0]?.length}*${curResolution?.[0]?.width}*${curResolution?.[0]?.superType}`
      )
      // midjourney
      setResolutionKey(options?.allSizeOptions?.[0]?.resolutionKey!)
    } else {
      setSelectSize(aiParams.size)
      setSelectResolution(aiParams.resolution)
      setResolutionKey(aiParams.resolutionKey)
    }

    if (isControlnet) {
      setPrefineOptions({
        width: Number(aiParams.resolution.split('*')[0] || 0),
        height: Number(aiParams.resolution.split('*')[1] || 0),
      })
    }

    setSelectModel(aiParams.model)
    setStyleModel(aiParams.styleModel)
    setSelectedControlPrefine(aiParams.superType)
    setPretreatmentMethod(aiParams.pretreatmentMethod)
    setStyleDecoration(aiParams.styleDecorationOptions || [])

    // 备注：底图不能是空字符串
    setInitImageUrl(aiParams.initImageUrl || undefined)
    setCreativityDegree(
      aiParams.creativityDegree ?? DEFAULT_OPTIONS.CREATIVITY_DEGREE
    )
    setUcPrompt(aiParams.ucPrompt)
    setCharacter(aiParams.character || [])
    setModelFusion(aiParams.modelFusion || [])
    setSelectSampler(
      typeof aiParams?.samplerModelInfo?.samplerIndex === 'number'
        ? [aiParams?.samplerModelInfo?.samplerIndex]
        : []
    )
    setCfg(aiParams.cfg)
    setClipSkip(aiParams.clipSkip)
    setEnsd(aiParams.ensd)

    /** 修手 */
    setDoRepairTheHand(!!aiParams.doRepairTheHand)
    setTrackEventLabel(aiParams.trackEventLabel)

    setStyleDiff(aiParams.mjParam?.chaos ?? 0)
    setStyleArtistic(aiParams.mjParam?.stylize ?? 100)
    setMjQuality(aiParams.mjParam?.quality || MJQuality.QUALITY_100)
    setMjUpbeta(aiParams.mjParam?.upbeta || false)
    setMjTile(aiParams.mjParam?.tile || false)

    /** 透明图参数 */
    setTransparentWeight(
      aiParams?.layerDiffusion?.weight ?? DEFAULT_OPTIONS?.TRANSPARENT_WEIGHT
    )
    setEndingStep(
      aiParams?.layerDiffusion?.endingStep ?? DEFAULT_OPTIONS?.ENDING_STEP
    )
    /** 各种参考图 */
    const instantStyleArray = aiParams?.instantStyle
    const rfStyle = instantStyleArray?.find(
      (item: any) => item.showType === ControlShowType.STYLE
    )
    const rfRole = instantStyleArray?.find(
      (item: any) => item.showType === ControlShowType.FACEID
    )
    const rfStructure = instantStyleArray?.find(
      (item: any) => item.showType === ControlShowType.COMPOSITION
    )
    setReferToStyle({
      url: rfStyle?.imageUrl || aiParams.mjParam?.srefUrls?.[0] || '',
      weight: rfStyle?.controlWeight ?? DEFAULT_OPTIONS.WEIGHT,
      mjWeight: aiParams.mjParam?.sw ?? DEFAULT_OPTIONS.MJ_WEIGHT,
    })
    setReferToRole({
      url: rfRole?.imageUrl || aiParams.mjParam?.crefUrls?.[0] || '',
      weight: rfRole?.controlWeight ?? DEFAULT_OPTIONS.ROLE_WEIGHT,
      pulidMode: rfRole?.pulidMode || DEFAULT_OPTIONS.PULID_MODE,
      mjWeight: aiParams.mjParam?.cw ?? DEFAULT_OPTIONS.MJ_WEIGHT,
    })
    setReferToStructure({
      url: rfStructure?.imageUrl || '',
      weight: rfStructure?.controlWeight ?? DEFAULT_OPTIONS.WEIGHT,
    })

    // 4k
    setCfgScale1(aiParams.cutDiffusion?.cfgScale1 || undefined)
    setCfgScale2(aiParams.cutDiffusion?.cfgScale2 || undefined)
    setCutDiffProportion(aiParams.cutDiffusion?.cutdiffProportion || undefined)

    // ifg
    if (
      !isNullOrUndefined(aiParams.flux?.chaos) ||
      !isNullOrUndefined(aiParams.flux?.style)
    ) {
      setChaos(aiParams.flux?.chaos)
      setStyle(aiParams.flux?.style)
    }

    console.log('aiParams', aiParams)
    console.log(
      'advance',
      (pattern === AiCreateArtworkPattern.GENERAL
        ? !!aiParams.initImageUrl
        : false) ||
        !!instantStyleArray?.length ||
        !!aiParams.modelFusion?.length ||
        !!aiParams.character?.length ||
        !!aiParams.cfg ||
        !!aiParams.ensd ||
        !!aiParams.clipSkip ||
        !!aiParams.seed ||
        !!aiParams.ucPrompt ||
        (!!aiParams.samplerModelInfo &&
          !!aiParams.samplerModelInfo.samplerModelName) ||
        !!aiParams.mjParam?.chaos ||
        !!aiParams.mjParam?.stylize ||
        !!aiParams.mjParam?.quality ||
        !!aiParams.mjParam?.upbeta ||
        !!aiParams.mjParam?.tile ||
        !!aiParams.layerDiffusion?.weight ||
        !!aiParams.layerDiffusion?.endingStep ||
        !!aiParams.cutDiffusion ||
        !!aiParams.flux?.chaos ||
        !!aiParams.flux?.style
    )
    setHasAdvance(
      (pattern === AiCreateArtworkPattern.GENERAL
        ? !!aiParams.initImageUrl
        : false) ||
        !!instantStyleArray?.length ||
        !!aiParams.modelFusion?.length ||
        !!aiParams.character?.length ||
        !!aiParams.cfg ||
        !!aiParams.ensd ||
        !!aiParams.clipSkip ||
        !!aiParams.seed ||
        !!aiParams.ucPrompt ||
        (!!aiParams.samplerModelInfo &&
          !!aiParams.samplerModelInfo.samplerModelName) ||
        !!aiParams.mjParam?.chaos ||
        !!aiParams.mjParam?.stylize ||
        !!aiParams.mjParam?.quality ||
        !!aiParams.mjParam?.upbeta ||
        !!aiParams.mjParam?.tile ||
        !!aiParams.layerDiffusion?.weight ||
        !!aiParams.layerDiffusion?.endingStep ||
        !!aiParams.mjParam?.srefUrls?.length ||
        !!aiParams.mjParam?.crefUrls?.length ||
        !!aiParams.cutDiffusion ||
        !!aiParams.flux?.chaos ||
        !!aiParams.flux?.style
    )

    clearStorage('aiParam')
    store.ai?.setIsAgain(false)
  }

  /** 来自别的页面的再来一张 */
  useEffect(() => {
    again()
  }, [])

  /** 来自本页面的再来一张 */
  useEffect(() => {
    if (store.ai?.isAgain) {
      again()
    }
  }, [store.ai?.isAgain])

  /** 咒语解析 */
  const getSpellAnalysisImgType = (
    spellAnalysisImgType?: SpellAnalysisImgType | null
  ) => {
    if (!hasAdvance || isST || !spellAnalysisImgType) {
      return
    }
    if (
      spellAnalysisImgType === SpellAnalysisImgType.REFERENCE &&
      !initImageUrl
    ) {
      // 参考图
      return
    } else if (
      spellAnalysisImgType === SpellAnalysisImgType.STYLE &&
      !referToStyle?.url
    ) {
      // 风格参考
      return
    } else if (
      spellAnalysisImgType === SpellAnalysisImgType.FACEID &&
      !referToRole?.url
    ) {
      // 角色参考
      return
    } else if (
      spellAnalysisImgType === SpellAnalysisImgType.COMPOSITION &&
      !referToStructure?.url
    ) {
      // 结构参考
      return
    }
    return spellAnalysisImgType
  }

  function getCutDiffusionParam() {
    if (!is4k) {
      return undefined
    }
    if (!hasAdvance) {
      return {}
    }
    return {
      cutdiffProportion: cutDiffProportion,
      cfgScale1: cfgScale1,
      cfgScale2: cfgScale2,
    }
  }

  function getFluxParam() {
    if (!isFlux) {
      return undefined
    }
    if (initImageUrl) {
      return undefined
    }
    if (!hasAdvance) {
      return undefined
    }
    return {
      chaos,
      style,
    }
  }

  const getParams: () => Promise<FormParamsProps> = async () => {
    let maskImageUrl: string | undefined
    let maskZoneImageUrl: string | undefined
    if (isMask) {
      const res = await paintRef.saveMask?.()

      if (!res?.[0]) {
        message.destroy()
        message.error('请上传图片')
        return Promise.reject()
      }

      console.log(res)

      maskImageUrl = res[0]
      maskZoneImageUrl = res[1]
    } else if (isControlnet && !initImageUrl) {
      message.destroy()
      message.error('请上传图片')
      return Promise.reject()
    }

    let width: number = 0
    let height: number = 0
    let superType: SuperType = SuperType.SUPERSIZE
    if (selectResolution) {
      const [a, b, s] = selectResolution.split('*')
      width = Number(a)
      height = Number(b)
      superType = s as SuperType
    }
    let size = selectSize
    let initWidth: number | undefined = undefined // 底图宽
    let initHeight: number | undefined = undefined // 底图高
    if (initImageUrl || maskImageUrl) {
      await new Promise<void>((resolve, reject) => {
        const img = new Image()
        img.src = initImageUrl || (maskImageUrl as string)
        img.onload = () => {
          initWidth = img.naturalWidth
          initHeight = img.naturalHeight
          resolve()
        }
        img.onerror = () => {
          message.destroy()
          message.error('获取底图宽度时出错')
          reject()
        }
      })
    }

    /** controlnet 没有尺寸，用底图尺寸处理 size resolution */
    if (isControlnet) {
      size = `${initWidth}:${initHeight}`
      width = initWidth!
      height = initHeight!
      superType = selectedControlPrefine as SuperType
    }

    /** 风格参考、结构参考、角色参考 */
    const instantStyle: Array<{
      showType: ControlShowType
      imageUrl: string
      controlWeight: number
      pulidMode?: ControlNetPulidMode
    }> = []
    const mjInstantStyle: {
      srefUrls?: string[]
      sw?: number
      crefUrls?: string[]
      cw?: number
    } = {}
    if (!isMJ && isSdxl) {
      referToRole?.url &&
        instantStyle.push({
          showType: ControlShowType.FACEID,
          imageUrl: referToRole.url,
          controlWeight: referToRole.weight,
          pulidMode: referToRole.pulidMode,
        })
      referToStructure?.url &&
        instantStyle.push({
          showType: ControlShowType.COMPOSITION,
          imageUrl: referToStructure.url,
          controlWeight: referToStructure.weight,
        })
      referToStyle?.url &&
        instantStyle.push({
          showType: ControlShowType.STYLE,
          imageUrl: referToStyle.url,
          controlWeight: referToStyle.weight,
        })
    }
    if (isMJ6) {
      if (referToStyle?.url) {
        mjInstantStyle.srefUrls = [referToStyle.url]
        mjInstantStyle.sw = referToStyle.mjWeight
      }
      if (referToRole?.url) {
        mjInstantStyle.crefUrls = [referToRole.url]
        mjInstantStyle.cw = referToRole.mjWeight
      }
    }
    if (isMJ7) {
      if (referToStyle?.url) {
        mjInstantStyle.srefUrls = [referToStyle.url]
        mjInstantStyle.sw = referToStyle.mjWeight
      }
    }

    return {
      pattern,
      modelCode: modelCode!,
      model: selectModel,
      prompt,
      size,
      width: height, // 高
      length: width, // 宽
      superType,
      styleModel: isST ? styleModel : undefined,
      styleDecorationOptions: !isST
        ? styleDecoration.map(i => ({ key: i.key, type: i.type }))
        : [],
      maskImageUrl: !isST ? maskImageUrl : undefined,
      maskZoneImageUrl: !isST ? maskZoneImageUrl : undefined,
      initImageUrl: !isST && initImageUrl && !is4k ? initImageUrl : undefined,
      initWidth:
        !isST && (initImageUrl || maskImageUrl) ? initWidth : undefined,
      initHeight:
        !isST && (initImageUrl || maskImageUrl) ? initHeight : undefined,
      creativityDegree:
        !isST && (initImageUrl || maskImageUrl) ? creativityDegree : undefined,
      ucPrompt:
        !isST && ucPrompt && ucPrompt.trim().length ? ucPrompt : undefined, // 避免传空字符串给后端
      character: !isST ? character : [],
      // modelFusion: !isST ? modelFusion : [],
      ...getModelFusionParams(!isST ? modelFusion : []),
      cfg: hasAdvance && !isST ? cfg : undefined,
      ensd: hasAdvance && !isST && !isFlux ? ensd : undefined,
      clipSkip: hasAdvance && !isST && !isFlux ? clipSkip : undefined,
      seed: hasAdvance && !isST ? seed : undefined,
      samplerIndex:
        hasAdvance && !isST && !is4k && !isFlux
          ? selectSampler?.[0]
          : undefined,
      mjParam:
        isMJ && hasAdvance
          ? {
              chaos: styleDiff,
              stylize: styleArtistic,
              quality: isMJ7 ? undefined : mjQuality,
              upbeta: isMJ7 ? undefined : mjUpbeta,
              tile: isMJ7 ? undefined : mjTile,
              ...mjInstantStyle,
            }
          : undefined,
      layerDiffusion:
        isTransparent && hasAdvance
          ? {
              weight: transparentWeight,
              endingStep: endingStep,
            }
          : undefined,
      pretreatmentMethod: isControlnet ? pretreatmentMethod : undefined,
      doRepairTheHand: !isST ? doRepairTheHand : undefined,
      num: paintTimes,
      queueType: genTidal ? QueueType.TIDAL : QueueType.NORMAL,
      trackEventLabel,
      instantStyle:
        hasAdvance && !isST && !is4k && !isFlux ? instantStyle : undefined,
      spellAnalysisImgType: getSpellAnalysisImgType(spellAnalysisImgType),
      cutdiffusion: getCutDiffusionParam(),
      initImageSources: isMask ? initImageSources : undefined,
      flux: getFluxParam(),
    }
  }

  const reset = () => {
    setGenTidal(false)
  }

  return (
    <>
      <MyForm layout="vertical" form={form} $dark className={styles.darkForm}>
        {/** Controlnet作画参数 */}
        {isControlnet && <ControlnetFormContent />}

        {/** 涂抹作画参数 */}
        {isMask && <MaskFormContent />}

        {/** 透明图作画参数 */}
        {isTransparent && <TransparentFormContent />}

        {/** 常规作画参数 */}
        {isGeneral && <NormalFormContent />}

        {/* 作画次数 */}
        <PaintTimesComp />

        {/* 生成方式 */}
        <GenerationComp />

        {/* 协议 */}
        <AgreementComp />
      </MyForm>

      {/* AI生成 */}
      <CreateComp reset={reset} getParams={getParams} />
    </>
  )
})

/**
 *
 * @param modelFusion 融合模型列表
 * @returns 参数对象
 */
export function getModelFusionParams(modelFusion?: any[] | null) {
  const _modelFusion: any[] = []
  const _userTrainedModel: any[] = []
  if (!modelFusion) {
    return {
      modelFusion: _modelFusion,
      userTrainedModel: _userTrainedModel,
    }
  }
  modelFusion.forEach(it => {
    if (it.key.startsWith('user_')) {
      _userTrainedModel.push({
        id: Number(it.key!.replace('user_', '')),
        weight: it.weight!,
      })
    } else {
      _modelFusion.push({
        key: it.key!,
        weight: it.weight!,
      })
    }
  })
  return {
    modelFusion: _modelFusion,
    userTrainedModel: _userTrainedModel,
  }
}
